import React, { useState, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  X,
  Lightbulb,
  Calendar,
  MessageSquare,
  BookOpen,
  FileText,
  Users,
  UserCog,
  FileCode,
  User,
  Home,
  DoorOpen,
  ArrowLeft,
  ChevronRight,
  Activity,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { LanguageSelectorModal } from '@/shared/components/LanguageSelectorModal';
import { HomeIcon } from '@/shared/icons/HomeIcon';
import { DevicesIcon } from '@/shared/icons/DevicesIcon';
import { AIAssistantIcon } from '@/shared/icons/AIAssistantIcon';
import { AIIcon } from '@/shared/icons/AIIcon';
import { BellIcon } from '@/shared/icons/BellIcon';
import { DeviceProvider } from '@/features/devices/hooks/useSelectedDevice';
import { PropertyProvider } from '@/features/properties/hooks/useSelectedProperty';
import { UnifiedPropertySelector } from '@/features/properties/components/UnifiedPropertySelector';
import { MobileTaskbar } from '@/shared/components/MobileTaskbar';
import { Sidebar } from '@/shared/components/Sidebar';
import { HamburgerIcon } from './HamburgerIcon';
import { getProfileRole } from '@/services/auth/auth.service';
import { getHomeownerContext } from '@/services/supabase/homeownerMembers.service';
import { PendingMemberInvitations } from '@/features/homeownerMembers/components/PendingMemberInvitations';
import { usePropertyAccess } from '@/hooks/usePropertyAccess';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/services/auth/AuthProvider';
import { useProfile } from '@/features/profile/hooks/useProfile';
import type { UserRole } from '@/shared/components/Sidebar/types';
import styles from './AppLayout.module.css';

// Hook to detect if we're on desktop or tablet/mobile.
// Breakpoint 1056px — single desktop↔tablet switch shared with the dashboard
// bento (#2884); below it the sidebar collapses to the hamburger.
const useIsDesktop = () => {
  const [isDesktop, setIsDesktop] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.innerWidth >= 1056;
    }
    return true;
  });

  useEffect(() => {
    const handleResize = () => {
      setIsDesktop(window.innerWidth >= 1056);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return isDesktop;
};

// Hook to detect if we're on tablet (768px - 1055px)
const useIsTablet = () => {
  const [isTablet, setIsTablet] = useState(() => {
    if (typeof window !== 'undefined') {
      const width = window.innerWidth;
      return width >= 768 && width < 1056;
    }
    return false;
  });

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      setIsTablet(width >= 768 && width < 1056);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return isTablet;
};

const AppLayoutContent = ({ children }: { children: React.ReactNode }) => {
  const location = useLocation();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isLanguageModalOpen, setIsLanguageModalOpen] = useState(false);
  const [userRole, setUserRole] = useState<UserRole>('homeowner');
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    'house': true,
    'ai': true,
    'management': true,
    'documentation': true,
    'user': true,
  });
  const { logout } = useAuth();
  const { profile } = useProfile();
  const { t, i18n } = useTranslation();
  const isDesktop = useIsDesktop();
  const isTablet = useIsTablet();

  const toggleSection = (section: string) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  // Fetch user role on mount
  useEffect(() => {
    const fetchRole = async () => {
      const role = await getProfileRole();
      if (role === 'technician') {
        setUserRole('technician');
      } else if (role === 'admin') {
        setUserRole('admin');
      } else {
        setUserRole('homeowner');
      }
    };
    fetchRole();
  }, []);

  const isTechnician = userRole === 'technician';
  const sp = (path: string) => isTechnician ? `/service-provider${path}` : path;

  // Gates the owner-only Members link (#2913). Must reflect the CURRENTLY
  // SELECTED property, not "owns any property": a user who owns Sant Blai but
  // is only a member of Es Revellar must NOT see Members while viewing Es
  // Revellar. usePropertyAccess().isOwner is per selected property (defaults
  // false until resolved → link stays hidden for members, appears for owners).
  const { isOwner } = usePropertyAccess();

  // canEdit = owner or editor member; view-only members can't edit anything.
  // Default true WHILE LOADING so owner controls don't flicker away, but fail
  // closed when the context query errored (e.g. no session): an error must
  // never hand a view-only member edit controls.
  const { data: homeownerCtx, isError: isHomeownerCtxError } = useQuery({
    queryKey: ['homeownerContext'],
    queryFn: getHomeownerContext,
    staleTime: 5 * 60 * 1000,
  });
  const canEdit = homeownerCtx ? homeownerCtx.canEdit : !isHomeownerCtxError;

  // House section items
  const houseItems = [
    { href: sp('/dashboard'), icon: LayoutDashboard, labelKey: 'sidebar.items.dashboard' },
    { href: sp('/devices'), icon: DevicesIcon, labelKey: 'sidebar.items.devices' },
    { href: sp('/network'), icon: Activity, labelKey: 'sidebar.items.network' },
    { href: sp('/access-control'), icon: DoorOpen, labelKey: 'sidebar.items.accessControl' },
  ];

  // AI section items
  const aiItems = [
    { href: sp('/ai-assistant'), icon: AIAssistantIcon, labelKey: 'sidebar.items.aiAssistant', beta: true },
    { href: sp('/optimizations'), icon: Lightbulb, labelKey: 'sidebar.items.optimizations', beta: true },
  ];

  // Management section items
  const managementItems = [
    { href: sp('/calendar'), icon: Calendar, labelKey: 'sidebar.items.calendar' },
    { href: sp('/chat'), icon: MessageSquare, labelKey: 'sidebar.items.chat' },
    isTechnician
      ? { href: '/service-provider/customers', icon: Users, labelKey: 'sidebar.items.customers' }
      : { href: '/homeowner', icon: Users, labelKey: 'sidebar.items.serviceProviders' },
    ...(isTechnician ? [{ href: '/service-provider/team', icon: UserCog, labelKey: 'sidebar.items.team' }] : []),
  ];

  // Documentation section items
  const documentationItems = [
    { href: isTechnician ? '/service-provider/documents' : '/my-documents', icon: FileCode, labelKey: 'sidebar.items.myDocuments', beta: false },
    { href: sp('/zentri-hub'), icon: BookOpen, labelKey: 'sidebar.items.zentriHub', beta: true },
  ];

  // User section items
  const userItems = [
    { href: isTechnician ? '/service-provider/profile' : '/home-owner/profile', icon: UserCog, labelKey: 'sidebar.items.profile' },
    // Properties tab temporarily hidden for homeowners (may come back later):
    ...(isTechnician ? [{ href: '/service-provider/property-profiles', icon: Home, labelKey: 'sidebar.items.properties' }] : []),
    // Members (invite by email) — owner-only, homeowners only (#2913).
    ...((!isTechnician && isOwner) ? [{ href: '/homeowner/members', icon: Users, labelKey: 'sidebar.items.members' }] : []),
    { href: sp('/alarms'), icon: BellIcon, labelKey: 'sidebar.items.alarms' },
  ];

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const closeSidebar = () => {
    setIsSidebarOpen(false);
  };

  const handleNavLinkClick = () => {
    setIsSidebarOpen(false);
  };

  const handleLogout = async () => {
    if (isLoggingOut) return;
    setIsLoggingOut(true);
    try {
      await logout();
    } catch (error) {
      console.error('Failed to logout', error);
    } finally {
      setIsLoggingOut(false);
    }
  };

  const handleBack = () => {
    closeSidebar();
    window.history.back();
  };

  const getInitials = (fullName: string | null | undefined): string => {
    if (!fullName) return '??';
    const names = fullName.trim().split(' ');
    if (names.length === 0) return '??';
    if (names.length === 1) return names[0].substring(0, 2).toUpperCase();
    return (names[0][0] + names[names.length - 1][0]).toUpperCase();
  };

  // Page detection for conditional rendering
  const pathname = location.pathname;
  const isAlarmsPage = pathname === '/alarms' || pathname.startsWith('/alarms/');
  const isAlarmRulesPage = pathname === '/alarms/rules';
  const isAlarmAIAssistantPage = pathname === '/alarms/ai-assistant';
  const isOptimizationsPage = pathname === '/optimizations';
  const isOptimizationDetailPage = pathname.startsWith('/optimizations/');
  const isTariffOptimizationPage = pathname === '/tariff-optimization';
  const isTariffDetailPage = pathname.startsWith('/tariff/');
  const isTariffListPage = pathname === '/tariff-list';
  const isNetworkPage = pathname === '/network';
  const isNetworkDetailPage = pathname.startsWith('/network/');
  const isHubPage = pathname === '/zentri-hub';
  const isHubDocumentPage = pathname.startsWith('/zentri-hub/');
  const isAIAssistantPage = pathname === '/ai-assistant' || pathname.startsWith('/ai-assistant/');
  const isAIAssistantDetailPage = pathname.startsWith('/ai-assistant/') && pathname !== '/ai-assistant/';
  const isChatPage = pathname === '/chat';
  const isChatDetailPage = pathname.startsWith('/chat/') && pathname !== '/chat/new';
  const isDevicesPage = pathname === '/devices';
  const isRoomDetailPage = pathname.startsWith('/devices/room/');
  const isDeviceTelemetryPage = pathname.startsWith('/devices/telemetry/');
  const isFloorPlanPage = pathname.startsWith('/floor-plan/');
  const isDashboardPage = pathname === '/dashboard';
  const isMyDocumentsPage = pathname === '/my-documents';
  const isUploadDocumentPage = pathname === '/upload-document';
  const isCreateClaimPage = pathname === '/create-claim';
  const isDocumentDetailPage = pathname.startsWith('/document-detail/');
  const isServiceProviderCustomersPage = pathname === '/service-provider/customers' || pathname.startsWith('/service-provider/customers/');
  const isServiceProviderDashboardPage = pathname === '/service-provider/dashboard';
  const isServiceProviderAlarmsPage = pathname === '/service-provider/alarms';
  const isServiceProviderDevicesPage = pathname === '/service-provider/devices';
  const isServiceProviderOptimizationsPage = pathname === '/service-provider/optimizations' || pathname.startsWith('/service-provider/optimizations/');
  const isServiceProviderAIAssistantPage = pathname === '/service-provider/ai-assistant' || pathname.startsWith('/service-provider/ai-assistant/');
  const isServiceProviderAIAssistantDetailPage = pathname.startsWith('/service-provider/ai-assistant/') && pathname !== '/service-provider/ai-assistant/';
  const isServiceProviderChatPage = pathname === '/service-provider/chat';
  const isServiceProviderProfilePage = pathname === '/service-provider/profile';
  const isServiceProviderPropertyPage = pathname === '/service-provider/property-profiles';
  const isServiceProviderTeamPage = pathname === '/service-provider/team';
  const isHomeownerPage = pathname === '/homeowner' || pathname.startsWith('/homeowner/');
  const isCalendarPage = pathname === '/calendar' || pathname.startsWith('/calendar/');
  const isProfilePage = pathname === '/home-owner/profile';
  const isProfileEditPage = pathname === '/home-owner/profile/edit';
  const isAccessControlPage = pathname === '/access-control' || pathname.startsWith('/access-control/');
  const isIntegrationsPage = pathname.startsWith('/integrations/');
  const isPropertyEditPage2 = pathname.startsWith('/home-owner/property/edit/');

  const [isAlarmDetailView, setIsAlarmDetailView] = React.useState(false);
  const [isAlarmArchiveView, setIsAlarmArchiveView] = React.useState(false);

  React.useEffect(() => {
    const checkViews = () => {
      const alarmDetailContainer = document.querySelector('[data-view="alarm-detail"]');
      const alarmArchiveContainer = document.querySelector('[data-view="alarm-archive"]');
      setIsAlarmDetailView(!!alarmDetailContainer);
      setIsAlarmArchiveView(!!alarmArchiveContainer);
    };

    checkViews();

    const observer = new MutationObserver(() => {
      checkViews();
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: false
    });

    return () => observer.disconnect();
  }, []);

  const shouldHideHamburger = isAlarmsPage || isAlarmRulesPage || isAlarmAIAssistantPage || isDeviceTelemetryPage || isRoomDetailPage || isFloorPlanPage || isOptimizationsPage || isOptimizationDetailPage || isTariffOptimizationPage || isTariffDetailPage || isTariffListPage || isNetworkPage || isNetworkDetailPage || isHubPage || isHubDocumentPage || isAIAssistantPage || isChatPage || isDevicesPage || isDashboardPage || isMyDocumentsPage || isUploadDocumentPage || isCreateClaimPage || isDocumentDetailPage || isServiceProviderCustomersPage || isServiceProviderDashboardPage || isServiceProviderAlarmsPage || isServiceProviderDevicesPage || isServiceProviderOptimizationsPage || isServiceProviderAIAssistantPage || isServiceProviderChatPage || isServiceProviderProfilePage || isServiceProviderPropertyPage || isServiceProviderTeamPage || isHomeownerPage || isProfilePage || isProfileEditPage || isCalendarPage || isAccessControlPage || isIntegrationsPage || isPropertyEditPage2;

  const shouldHideMobileTaskbar = (isAlarmsPage && (isAlarmDetailView || isAlarmArchiveView)) || isAlarmAIAssistantPage || isOptimizationDetailPage || isNetworkPage || isNetworkDetailPage || (isAIAssistantPage && isTablet) || isChatDetailPage || isAIAssistantDetailPage || isServiceProviderAIAssistantDetailPage;

  return (
    <div className={styles.layout} data-hide-hamburger={shouldHideHamburger}>
      {/* Hamburger Menu Button - Mobile Only */}
      <button
        // #3534: pages locate this button by id — never by its (translated) label.
        id="app-layout-hamburger"
        className={styles.hamburger}
        onClick={toggleSidebar}
        aria-label={isSidebarOpen ? t('dashboard.actions.closeMenu', 'Close menu') : t('dashboard.actions.openMenu', 'Open menu')}
      >
        {isSidebarOpen ? <X size={24} /> : <HamburgerIcon size={24} />}
      </button>

      {/* Overlay - Mobile Only */}
      {isSidebarOpen && (
        <div className={styles.overlay} onClick={closeSidebar} />
      )}

      {/* Desktop/iPad: New Sidebar */}
      {isDesktop && (
        <Sidebar
          role={userRole}
          isOpen={true}
          onClose={closeSidebar}
          variant={(isAIAssistantPage || isServiceProviderAIAssistantPage) ? 'ai' : 'default'}
        />
      )}

      {/* Mobile: Old Sidebar (Drawer) */}
      {!isDesktop && (
        <aside className={`${styles.sidebar} ${isSidebarOpen ? styles.sidebarOpen : ''}`}>
          {/* Top Bar - Back arrow and property selector */}
          <div className={styles.topBar}>
            <button
              type="button"
              className={styles.backButton}
              onClick={handleBack}
              aria-label="Zurück"
            >
              <ArrowLeft size={24} />
            </button>

            <div className={styles.propertySelector}>
              <UnifiedPropertySelector />
            </div>
            <div className={styles.languageSwitcher}>
              <button
                type="button"
                className={styles.languageButton}
                onClick={() => setIsLanguageModalOpen(true)}
                aria-label={t('sidebar.selectLanguage', 'Select language')}
              >
                <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M6.66666 10.6666L14.6667 18.6666" stroke="#262626" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M5.33334 18.6666L13.3333 10.6666L16 6.66663" stroke="#262626" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M2.66666 6.66663H18.6667" stroke="#262626" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M9.33334 2.66663H10.6667" stroke="#262626" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M29.3333 29.3333L22.6667 16L16 29.3333" stroke="#262626" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M18.6667 24H26.6667" stroke="#262626" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <span>{(i18n.language || 'en').substring(0, 2).toUpperCase()}</span>
              </button>
            </div>
          </div>

          {/* Profile Section */}
          <div className={styles.profileSection}>
            <div className={styles.profileAvatar}>
              {profile?.avatar_url ? (
                <img
                  src={profile.avatar_url}
                  alt={profile.full_name || 'Profile'}
                  className={styles.profileAvatarImage}
                  key={profile.avatar_url}
                />
              ) : (
                getInitials(profile?.full_name)
              )}
            </div>
            <div className={styles.profileInfo}>
              <p className={styles.profileName}>{profile?.full_name || 'Loading...'}</p>
              <div className={styles.profileButtons}>
                {canEdit && (
                  <NavLink to="/home-owner/profile/edit" className={styles.profileEditButton} onClick={handleNavLinkClick}>
                    {t('profile.editProfile')}
                  </NavLink>
                )}
                <button
                  type="button"
                  className={styles.profileSignOutButton}
                  onClick={handleLogout}
                  disabled={isLoggingOut}
                >
                  {isLoggingOut ? t('profile.signingOut') : t('profile.signOut')}
                </button>
              </div>
            </div>
          </div>

          <nav className={styles.nav}>
            {/* House Section */}
            <div className={styles.navSection}>
              <div
                className={styles.navSectionHeader}
                onClick={() => toggleSection('house')}
              >
                <HomeIcon size={16} />
                <span>{t('sidebar.groups.house')}</span>
                <ChevronRight
                  size={16}
                  className={`${styles.navSectionArrow} ${expandedSections['house'] ? styles.open : ''}`}
                />
              </div>
              <div className={`${styles.navSectionContent} ${expandedSections['house'] ? styles.open : ''}`}>
                {houseItems.map((item) => (
                  <NavLink
                    key={item.href}
                    to={item.href}
                    onClick={handleNavLinkClick}
                    className={({ isActive }) =>
                      `${styles.navLink} ${styles.navSubLink} ${isActive ? styles.active : ''}`
                    }
                  >
                    <item.icon size={16} />
                    <span>{t(item.labelKey)}</span>
                  </NavLink>
                ))}
              </div>
            </div>

            {/* AI Section */}
            <div className={styles.navSection}>
              <div
                className={styles.navSectionHeader}
                onClick={() => toggleSection('ai')}
              >
                <AIIcon size={16} />
                <span>{t('sidebar.groups.ai')}</span>
                <ChevronRight
                  size={16}
                  className={`${styles.navSectionArrow} ${expandedSections['ai'] ? styles.open : ''}`}
                />
              </div>
              <div className={`${styles.navSectionContent} ${expandedSections['ai'] ? styles.open : ''}`}>
                {aiItems.map((item) => (
                  <NavLink
                    key={item.href}
                    to={item.href}
                    onClick={handleNavLinkClick}
                    className={({ isActive }) =>
                      `${styles.navLink} ${styles.navSubLink} ${isActive ? styles.active : ''}`
                    }
                  >
                    <item.icon size={16} />
                    <span>
                      {t(item.labelKey)}
                      {item.beta && <span className={styles.navBeta}>{t('sidebar.beta', 'Beta')}</span>}
                    </span>
                  </NavLink>
                ))}
              </div>
            </div>

            {/* Management Section */}
            <div className={styles.navSection}>
              <div
                className={styles.navSectionHeader}
                onClick={() => toggleSection('management')}
              >
                <BellIcon size={16} />
                <span>{t('sidebar.groups.management')}</span>
                <ChevronRight
                  size={16}
                  className={`${styles.navSectionArrow} ${expandedSections['management'] ? styles.open : ''}`}
                />
              </div>
              <div className={`${styles.navSectionContent} ${expandedSections['management'] ? styles.open : ''}`}>
                {managementItems.map((item) => (
                  <NavLink
                    key={item.href}
                    to={item.href}
                    onClick={handleNavLinkClick}
                    className={({ isActive }) =>
                      `${styles.navLink} ${styles.navSubLink} ${isActive ? styles.active : ''}`
                    }
                  >
                    <item.icon size={16} />
                    <span>{t(item.labelKey)}</span>
                  </NavLink>
                ))}
              </div>
            </div>

            {/* Documentation Section */}
            <div className={styles.navSection}>
              <div
                className={styles.navSectionHeader}
                onClick={() => toggleSection('documentation')}
              >
                <FileText size={16} />
                <span>{t('sidebar.groups.documentation')}</span>
                <ChevronRight
                  size={16}
                  className={`${styles.navSectionArrow} ${expandedSections['documentation'] ? styles.open : ''}`}
                />
              </div>
              <div className={`${styles.navSectionContent} ${expandedSections['documentation'] ? styles.open : ''}`}>
                {documentationItems.map((item) => (
                  <NavLink
                    key={item.href}
                    to={item.href}
                    onClick={handleNavLinkClick}
                    className={({ isActive }) =>
                      `${styles.navLink} ${styles.navSubLink} ${isActive ? styles.active : ''}`
                    }
                  >
                    <item.icon size={16} />
                    <span>
                      {t(item.labelKey)}
                      {item.beta && <span className={styles.navBeta}>{t('sidebar.beta', 'Beta')}</span>}
                    </span>
                  </NavLink>
                ))}
              </div>
            </div>

            {/* User Section */}
            <div className={styles.navSection}>
              <div
                className={styles.navSectionHeader}
                onClick={() => toggleSection('user')}
              >
                <User size={16} />
                <span>{t('sidebar.groups.user')}</span>
                <ChevronRight
                  size={16}
                  className={`${styles.navSectionArrow} ${expandedSections['user'] ? styles.open : ''}`}
                />
              </div>
              <div className={`${styles.navSectionContent} ${expandedSections['user'] ? styles.open : ''}`}>
                {userItems.map((item) => (
                  <NavLink
                    key={item.href}
                    to={item.href}
                    onClick={handleNavLinkClick}
                    className={({ isActive }) =>
                      `${styles.navLink} ${styles.navSubLink} ${isActive ? styles.active : ''}`
                    }
                  >
                    <item.icon size={16} />
                    <span>{t(item.labelKey)}</span>
                  </NavLink>
                ))}
              </div>
            </div>
          </nav>
        </aside>
      )}

      <main className={styles.main}>
        <PendingMemberInvitations />
        {children}
      </main>

      {/* Mobile bottom navigation */}
      {!shouldHideMobileTaskbar && <MobileTaskbar />}

      {/* Language Selection Modal */}
      <LanguageSelectorModal isOpen={isLanguageModalOpen} onClose={() => setIsLanguageModalOpen(false)} />
    </div>
  );
};

// Main component wrapped with PropertyProvider and DeviceProvider
export const AppLayout = ({ children }: { children: React.ReactNode }) => {
  return (
    <PropertyProvider>
      <DeviceProvider>
        <AppLayoutContent>{children}</AppLayoutContent>
      </DeviceProvider>
    </PropertyProvider>
  );
};
